// tslint:disable:no-null-keyword

export interface StackTraceLine {
    file: string;
    methodName: string;
    arguments: Array<any>;
    lineNumber: number;
    column: number;
}

/**
 * Inspired by https://raw.githubusercontent.com/errwischt/stacktrace-parser/master/src/stack-trace-parser.js
 */
export class StackTraceParser {

    static UNKNOWN_FUNCTION = '<unknown>';
    static chromeRe = /^\s*at (.*?) ?\(((?:file|https?|blob|chrome-extension|native|eval|webpack|<anonymous>|\/|[a-z]:\\|\\\\).*?)(?::(\d+))?(?::(\d+))?\)?\s*$/i;
    static chromeEvalRe = /\((\S*)(?::(\d+))(?::(\d+))\)/;
    static winjsRe = /^\s*at (?:((?:\[object object\])?.+) )?\(?((?:file|ms-appx|https?|webpack|blob):.*?):(\d+)(?::(\d+))?\)?\s*$/i;
    static geckoRe = /^\s*(.*?)(?:\((.*?)\))?(?:^|@)((?:file|https?|blob|chrome|webpack|resource|\[native).*?|[^@]*bundle)(?::(\d+))?(?::(\d+))?\s*$/i;
    static geckoEvalRe = /(\S+) line (\d+)(?: > eval line \d+)* > eval/i;
    static javaScriptCoreRe = /^\s*(?:([^@]*)(?:\((.*?)\))?@)?(\S.*?):(\d+)(?::(\d+))?\s*$/i;
    static nodeRe = /^\s*at (?:((?:\[object object\])?[^\\/]+(?: \[as \S+\])?) )?\(?(.*?):(\d+)(?::(\d+))?\)?\s*$/i;

    /**
     * This parses the different stack traces and puts them into one format
     * This borrows heavily from TraceKit (https://github.com/csnover/TraceKit)
     */
    static parse(line: string): StackTraceLine {
        return StackTraceParser.parseChrome(line) ||
            StackTraceParser.parseWinjs(line) ||
            StackTraceParser.parseGecko(line) ||
            StackTraceParser.parseNode(line) ||
            StackTraceParser.parseJSC(line);
    }

    static parseChrome(line): StackTraceLine {
        const parts = StackTraceParser.chromeRe.exec(line);

        if (!parts) {
            return null;
        }

        const isNative = parts[2] && parts[2].indexOf('native') === 0; // start of line
        const isEval = parts[2] && parts[2].indexOf('eval') === 0; // start of line

        const subMatch = StackTraceParser.chromeEvalRe.exec(parts[2]);
        if (isEval && subMatch != null) {
            // throw out eval line/column and use top-most line/column number
            parts[2] = subMatch[1]; // url
            parts[3] = subMatch[2]; // line
            parts[4] = subMatch[3]; // column
        }

        return {
            file: !isNative ? parts[2] : null,
            methodName: parts[1] || StackTraceParser.UNKNOWN_FUNCTION,
            arguments: isNative ? [parts[2]] : [],
            lineNumber: parts[3] ? +parts[3] : null,
            column: parts[4] ? +parts[4] : null
        };
    }

    static parseWinjs(line): StackTraceLine {
        const parts = StackTraceParser.winjsRe.exec(line);

        if (!parts) {
            return null;
        }

        return {
            file: parts[2],
            methodName: parts[1] || StackTraceParser.UNKNOWN_FUNCTION,
            arguments: [],
            lineNumber: +parts[3],
            column: parts[4] ? +parts[4] : null
        };
    }

    static parseGecko(line): StackTraceLine {
        const parts = StackTraceParser.geckoRe.exec(line);

        if (!parts) {
            return null;
        }

        const isEval = parts[3] && parts[3].indexOf(' > eval') > -1;

        const subMatch = StackTraceParser.geckoEvalRe.exec(parts[3]);
        if (isEval && subMatch != null) {
            // throw out eval line/column and use top-most line number
            parts[3] = subMatch[1];
            parts[4] = subMatch[2];
            parts[5] = null; // no column when eval
        }

        return {
            file: parts[3],
            methodName: parts[1] || StackTraceParser.UNKNOWN_FUNCTION,
            arguments: parts[2] ? parts[2].split(',') : [],
            lineNumber: parts[4] ? +parts[4] : null,
            column: parts[5] ? +parts[5] : null
        };
    }

    static parseJSC(line): StackTraceLine {
        const parts = StackTraceParser.javaScriptCoreRe.exec(line);

        if (!parts) {
            return null;
        }

        return {
            file: parts[3],
            methodName: parts[1] || StackTraceParser.UNKNOWN_FUNCTION,
            arguments: [],
            lineNumber: +parts[4],
            column: parts[5] ? +parts[5] : null
        };
    }

    static parseNode(line): StackTraceLine {
        const parts = StackTraceParser.nodeRe.exec(line);

        if (!parts) {
            return null;
        }

        return {
            file: parts[2],
            methodName: parts[1] || StackTraceParser.UNKNOWN_FUNCTION,
            arguments: [],
            lineNumber: +parts[3],
            column: parts[4] ? +parts[4] : null
        };
    }
}



// WEBPACK FOOTER //
// ./src/app/lib/stack-trace-parser.ts